'use strict';

/**
 * NACRE Wave 2.3.2 — signup-client
 *
 * Pure function that runs in the user's browser at signup. Builds the
 * complete identity-vault wire payload from a password and (optionally)
 * a fixed mnemonic.
 *
 * What this function does (mirroring identity-vault.createIdentityVault
 * but as a pure return-value-only function with no I/O):
 *
 *   1. Generate or accept a 12-word BIP39 mnemonic
 *   2. Derive the BIP39 seed bytes
 *   3. Derive the wallet keypair via BIP32 at m/44'/8453'/0'/0/0
 *   4. Generate a fresh hybrid KEX keypair (X25519 + ML-KEM-768)
 *   5. Generate a fresh hybrid sign keypair (Ed25519 + ML-DSA-65)
 *   6. Generate a random 32-byte identityMaster
 *   7. Wrap identityMaster two ways: under Argon2id(password) and under
 *      HKDF(seed, label="vault:identity:v1")
 *   8. Encrypt the inner contents (private keys + empty moduleMasters +
 *      empty policies) under identityMaster as vault.bin
 *   9. Build the meta with public keys, salts, wraps, recovery fingerprint
 *  10. Return { meta, vaultBin, mnemonic, publicKeys, walletAddress }
 *
 * The CALLER (browser) is responsible for:
 *   - Showing the mnemonic to the user exactly once
 *   - Submitting { meta, vaultBin, walletAddress, publicKeys, passwordHash }
 *     to the server's vault-registration endpoint
 *   - Discarding the mnemonic + password from memory after submission
 *
 * The server NEVER sees: the mnemonic, the password (only its hash), the
 * unwrapped identityMaster, the unwrapped private keys.
 *
 * IMPORTANT: this module imports only @noble/* and @scure/* packages
 * (pure JS, browser-safe) plus our own primitives. Do NOT import Node-only
 * APIs here — this file must be browserifiable. The one exception is
 * `crypto.randomBytes` which is used via the same `crypto` builtin Node
 * exposes; the browser equivalent is `crypto.getRandomValues` and the
 * caller must polyfill it (or we use a small wrapper). For Wave 2.3 the
 * tests run in Node, so Node's `crypto` is fine.
 */

const crypto = require('crypto');

const { argon2id, hkdfSubkey, KDF_PROFILES } = require('./kdf');
const symmetric = require('./symmetric');
const { deriveAuthProof } = require('./auth-proof');
const { recoveryFingerprint } = require('./recovery-fingerprint');
const { generateMnemonic, mnemonicToSeed, validateMnemonic, deriveWalletKey, BASE_DERIVATION_PATH } = require('./seed');
const hybridKex = require('./hybrid-kex');
const hybridSign = require('./hybrid-sign');
const { VAULT_ALGO_V1 } = require('./algo-versions');

const SEED_LABEL_IDENTITY = 'oyster:vault:identity:v1';
const IDENTITY_MODULE_ID = 'identity';
const SCHEMA_VERSION = 1;

function nowIso() {
  return new Date().toISOString();
}

function sha256Hex(input) {
  return crypto.createHash('sha256').update(input).digest('hex');
}

// recoveryFingerprint is now the shared, stretched, per-account-salted deriver
// imported from ./recovery-fingerprint (T210 W3). It takes (mnemonic, {saltSeed}).

function deriveKeyPw(password, saltPw, kdfProfile) {
  return argon2id(password, saltPw, kdfProfile || KDF_PROFILES.DESKTOP);
}

function deriveKeySeed(mnemonic, saltSeed) {
  const seedBytes = mnemonicToSeed(mnemonic);
  const combined = new Uint8Array(seedBytes.length + saltSeed.length);
  combined.set(seedBytes, 0);
  combined.set(saltSeed, seedBytes.length);
  return hkdfSubkey(combined, SEED_LABEL_IDENTITY, 32);
}

// JSON cannot represent Uint8Array natively. Use a discriminated tag.
function serializeForStorage(value) {
  if (value instanceof Uint8Array) {
    return { __u8: Buffer.from(value).toString('hex') };
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const out = {};
    for (const k of Object.keys(value)) out[k] = serializeForStorage(value[k]);
    return out;
  }
  if (Array.isArray(value)) return value.map(serializeForStorage);
  return value;
}

function publicSidePublicKeys(kexKp, signKp) {
  return {
    kex: {
      x25519: kexKp.publicKey.x25519,
      mlKem: kexKp.publicKey.mlKem,
    },
    sign: {
      ed25519: signKp.publicKey.ed25519,
      mlDsa: signKp.publicKey.mlDsa,
    },
  };
}

function privateSidePrivateKeys(kexKp, signKp) {
  return {
    kex: {
      x25519: kexKp.privateKey.x25519,
      mlKem: kexKp.privateKey.mlKem,
    },
    sign: {
      ed25519: signKp.privateKey.ed25519,
      mlDsa: signKp.privateKey.mlDsa,
    },
  };
}

function walletAddressFromKey(publicKey) {
  // Compressed secp256k1 pubkey is 33 bytes. The conventional Ethereum-style
  // address is keccak256(uncompressed-pubkey-without-0x04-prefix)[12..32].
  // We don't yet need an Ethereum address — the wallet address used here is
  // a deterministic hex fingerprint of the compressed pubkey. Wave 5 will
  // refine this when wallet ops actually go on-chain.
  return '0x' + sha256Hex(Buffer.from(publicKey)).slice(0, 40);
}

/**
 * Build the complete signup wire payload.
 *
 * @param {object} args
 * @param {string} args.password — user-chosen password (never sent to server)
 * @param {string} [args.mnemonic] — optional fixed mnemonic for deterministic tests
 * @param {object} [args.kdfProfile] — Argon2id parameters (defaults to DESKTOP)
 * @returns {{
 *   meta: object,
 *   vaultBin: object,
 *   mnemonic: string,
 *   publicKeys: object,
 *   walletAddress: string
 * }}
 */
function buildSignupPayload({ password, mnemonic, kdfProfile }) {
  if (typeof password !== 'string' || password.length === 0) {
    throw new Error('Invalid password: must be a non-empty string');
  }
  let m = mnemonic;
  if (!m) {
    m = generateMnemonic();
  } else if (!validateMnemonic(m)) {
    throw new Error('Invalid mnemonic: failed BIP39 validation');
  }

  // 1. Generate keypairs
  const kexKp = hybridKex.generateKeyPair();
  const signKp = hybridSign.generateKeyPair();

  // 2. Derive wallet from mnemonic via BIP32
  const seedBytes = mnemonicToSeed(m);
  const walletKey = deriveWalletKey(seedBytes, BASE_DERIVATION_PATH);
  const walletAddress = walletAddressFromKey(walletKey.publicKey);

  // 3. Generate identityMaster + salts
  const identityMaster = new Uint8Array(crypto.randomBytes(32));
  const saltPw = crypto.randomBytes(16);
  const saltSeed = crypto.randomBytes(16);

  // 4. Derive wrap keys
  const keyPw = deriveKeyPw(password, saltPw, kdfProfile);
  const keySeed = deriveKeySeed(m, saltSeed);

  // 5. Wrap identityMaster two ways
  const wrapPw = symmetric.encrypt(identityMaster, keyPw);
  const wrapSeed = symmetric.encrypt(identityMaster, keySeed);

  // T589 / LD-10 — additively retain the MNEMONIC itself wrapped under the SAME
  // password-derived key. This is what makes "I'll save it later" real: after
  // an unlock, settings can decrypt this and reveal the 12 words. The server
  // never sees the words — it stores only this ciphertext (c0c preserved). It
  // is present in EVERY recovery state (saved/deferred/declined) so the three
  // states are cryptographically indistinguishable on disk (LD-9), never an
  // oracle. wrapSeed already unconditionally exists (LD-10) and is untouched.
  const wrapMnemonicPw = symmetric.encrypt(m, keyPw);

  // 6. Build inner contents (private keys + empty moduleMasters + empty policies)
  const inner = serializeForStorage({
    privateKeys: privateSidePrivateKeys(kexKp, signKp),
    moduleMasters: {},
    policies: {},
  });
  const vaultBin = symmetric.encrypt(JSON.stringify(inner), identityMaster);

  // 7. Build the meta (the publishable side)
  const publicKeys = publicSidePublicKeys(kexKp, signKp);
  const meta = {
    schemaVersion: SCHEMA_VERSION,
    moduleId: IDENTITY_MODULE_ID,
    algo: VAULT_ALGO_V1,
    createdAt: nowIso(),
    updatedAt: nowIso(),
    saltPw: Buffer.from(saltPw).toString('hex'),
    saltSeed: Buffer.from(saltSeed).toString('hex'),
    wrapPw,
    wrapSeed,
    wrapMnemonicPw, // T589 / LD-10 — password-wrapped mnemonic for later reveal
    publicKeys: serializeForStorage(publicKeys),
    // T210 W3: stretched, per-account-salted fingerprint. Pinned profile
    // (deriver default = DESKTOP) so signup and recovery derive identically
    // regardless of device — NOT the device-detected password kdfProfile.
    recoveryFingerprint: recoveryFingerprint(m, { saltSeed }),
  };

  return {
    meta,
    vaultBin,
    mnemonic: m,
    publicKeys,
    walletAddress,
  };
}

module.exports = {
  buildSignupPayload,
  deriveAuthProof,
  // Re-export the shared stretched fingerprint deriver (T210 W3) for lockstep
  // verification across signup/recovery/vault.
  recoveryFingerprint,
};
